// FormTemplate.js
import React, { useState, useEffect } from 'react';
import {
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  FormControl,
  Tooltip,
} from '@mui/material';

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 200,
      width: 250,
    },
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
};

const FormTemplate = ({ fields = [], formData, onChange }) => {
  const [options, setOptions] = useState({});

  useEffect(() => {
    const initialOptions = {};

    fields.forEach((field) => {
      if (field.type === 'select') {
        if (field.fetchUrl) {
          fetchOptions(field);
        } else if (field.options) {
          initialOptions[field.name] = field.options;
        }
      }
    });

    setOptions(initialOptions);
  }, [fields]);

  const fetchOptions = async (field) => {
    try {
      const response = await fetch(field.fetchUrl, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`Failed to fetch ${field.label}`);
      const data = await response.json();
      setOptions((prev) => ({ ...prev, [field.name]: data || [] }));
    } catch (error) {
      console.error(`Error fetching ${field.label}:`, error);
      setOptions((prev) => ({ ...prev, [field.name]: [] }));
    }
  };

  const handleInputChange = (name, value) => {
    onChange(name, value);
  };

  const labelWidths = fields.map((field) => field.label.length);
  const maxLabelWidth = Math.max(...labelWidths);

  return (
    <div className="space-y-6 p-5 lg:p-10">
      {fields.map((field) => {
        const optionLabelKey = field.optionLabel || 'label';
        const optionValueKey = field.optionValue || 'value';

        return (
          <div key={field.name} className="flex items-center space-x-4 mb-6">
            <Tooltip title={`This field is for ${field.label}`}>
              <div
                className="border border-gray-400 rounded-md px-4 py-3"
                style={{ width: `${maxLabelWidth + 2}ch` }}
              >
                <label className="text-black font-bold font-sans text-xs">
                  {field.label}
                </label>
              </div>
            </Tooltip>
            <div className="flex-grow">
              {field.type === 'select' ? (
                <FormControl fullWidth>
                  <Select
                    name={field.name}
                    value={formData[field.name]}
                    onChange={(e) =>
                      handleInputChange(field.name, e.target.value)
                    }
                    MenuProps={MenuProps}
                    displayEmpty
                    className="w-full bg-gray-100 border border-gray-400 rounded-sm p-2 focus:outline-none ring-2 ring-gray-400 h-12"
                    inputProps={{
                      className: 'h-8',
                    }}
                    renderValue={(selected) => {
                      const option = (options[field.name] || []).find(
                        (opt) => opt[optionValueKey] === selected
                      );
                      return option
                        ? option[optionLabelKey]
                        : `Select ${field.label}`;
                    }}
                  >
                    {(options[field.name] || []).map((option) => (
                      <MenuItem
                        key={String(option[optionValueKey])}
                        value={option[optionValueKey]}
                      >
                        <ListItemText primary={option[optionLabelKey]} />
                        <Checkbox
                          checked={
                            formData[field.name] === option[optionValueKey]
                          }
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <input
                  type={field.type}
                  name={field.name}
                  value={formData[field.name] || ''}
                  onChange={(e) =>
                    handleInputChange(field.name, e.target.value)
                  }
                  className="w-full p-2 bg-gray-100 border-2 ring-2 ring-gray-400 rounded-sm focus:outline-none h-12"
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FormTemplate;
